import React, { useState } from 'react';
import { Box, ExpandableSection, SpaceBetween, StatusIndicator, StatusIndicatorProps } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { CheckStatus, CoverageCheckResult } from '../../hooks/useFolderChecks';
import { EmployeePopoverCard } from '../../../ea-huddle-v2/components/attendees/EmployeePopoverCard';
import './CoverageRow.scss';

/** Maps an advisory CheckStatus to a Cloudscape StatusIndicator type. */
export const indicatorType = (status: CheckStatus): StatusIndicatorProps.Type =>
  status === 'success' ? 'success' : status === 'error' ? 'error' : status === 'warning' ? 'warning' : 'pending';

export interface CoverageRowProps {
  /** Label prefix for the summary line (e.g. the folder/category name). */
  title: string;
  /** The coverage result: covered/missing logins plus a rolled-up status. */
  result: CoverageCheckResult;
}

/**
 * A single coverage check row for an OPTIONAL source folder: an info StatusIndicator summarizing how
 * many selected people have material added, with an ExpandableSection listing the not-yet-added
 * people as avatars (collapsed by default) when any are missing. Each avatar shows the person's
 * detail on hover via EmployeePopoverCard.
 *
 * The severity is always `info` (never `warning`) — supplemental material is optional, so partial
 * coverage is informational, not a problem to flag. Recovered from the former ReadinessCheckList;
 * now reused inline in Step 4 next to the matching card-category checkbox.
 */
export const CoverageRow: React.FC<CoverageRowProps> = ({ title, result }) => {
  const [expanded, setExpanded] = useState(false);
  const total = result.covered.length + result.missing.length;

  return (
    // Class-bearing wrapper so the scoped SCSS can shrink the "Not added yet (N)" ExpandableSection
    // header text (Cloudscape's footer-variant header inherits font-size).
    <div className="coverage-row">
      <SpaceBetween size="xxs">
        <StatusIndicator type="info">{`Added for ${result.covered.length} of ${total} people · optional`}</StatusIndicator>

        {result.missing.length > 0 && (
          <ExpandableSection
            variant="footer"
            headerText={`Missing (${result.missing.length})`}
            expanded={expanded}
            headingTagOverride="h3"
            onChange={() => setExpanded((prev) => !prev)}
          >
            {/* One employee per line (vertical stack) rather than a wrapping horizontal row. */}
            <SpaceBetween size="xs">
              {result.missing.map((login) => (
                <EmployeePopoverCard key={login} loginName={login} renderWithPortal>
                  <Avatar username={login} size={AvatarSize.ExtraSmall} imageAltText={`Profile image of ${login}`} />
                </EmployeePopoverCard>
              ))}
            </SpaceBetween>
          </ExpandableSection>
        )}
      </SpaceBetween>
    </div>
  );
};
